import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { useIFixitApiClient } from '@ifixit/ifixit-api-client';
import { Duration } from '@ifixit/utils';
import { useCacheableQuery } from '@ifixit/utils-react/hooks';
import { Badge } from '@ifixit/ui/components/badge';
import { Button } from '@ifixit/ui/components/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@ifixit/ui/components/dialog';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import { _js } from '@ifixit/utils/localize';
import initializeComponent from 'Shared/react-initialize';
import { Recaptcha } from 'Shared/recaptcha';
import { useRef, useState, type FormEvent, type RefObject } from 'react';
import { z } from 'zod';
import { useLatestDefined } from 'Shared/hooks/use-latest-defined';

const MAX_DISTANCE_MILES = 75;
const NEAREST_SHOP_LIMIT = 3;

const ProShopSchema = z.object({
   id: z.string(),
   name: z.string(),
   address: z.string(),
   city: z.string(),
   state: z.string(),
   postcode: z.string(),
   country: z.string(),
   latitude: z.number(),
   longitude: z.number(),
   distanceMiles: z.number(),
   affiliation: z.enum(['microcenter', 'independent', 'repairdesk']),
   consultationUrl: z.string().optional(),
});
type ProShop = z.infer<typeof ProShopSchema>;

const ProShopListSchema = z.array(ProShopSchema);

type Coordinates = { latitude: number; longitude: number };

type ProLeadSectionProps = {
   deviceName: string;
   guideTitle: string;
   guideid: number;
   imageUrl: string;
   userName: string | null;
   userEmail: string | null;
   recaptchaSiteKey: string | null;
};

export function ProLeadSection({
   deviceName,
   guideTitle,
   guideid,
   imageUrl,
   userName,
   userEmail,
   recaptchaSiteKey,
}: ProLeadSectionProps) {
   const [coords, setCoords] = useState<Coordinates | null>(debugCoords());
   const nearestShops = useNearestProShops({
      latitude: coords?.latitude ?? null,
      longitude: coords?.longitude ?? null,
   });
   const shops = useLatestDefined(nearestShops);
   // Opening the form also asks for precise coordinates, which can name a
   // different shop mid-form. The lead belongs to the shop the user picked, so
   // hold onto it rather than following the list.
   const [leadFormShop, setLeadFormShop] = useState<ProShop | null>(null);
   // Hold onto the list the user tapped from so it can't empty out when
   // coordinates change.
   const [tappedShops, setTappedShops] = useState<ProShop[] | null>(null);
   const recaptchaRef = useRef<HTMLDivElement>(null);

   const visibleShops = pickVisibleShops(shops ?? [], tappedShops, coords);

   if (visibleShops.length === 0) {
      return null;
   }

   const requestPreciseCoords = () => {
      navigator.geolocation.getCurrentPosition(
         position => {
            setCoords(position.coords);
         },
         positionError => {
            console.error(
               'Failed to get current position for repair shop locator',
               positionError.code,
               positionError.message
            );
         },
         { enableHighAccuracy: true, timeout: 5000, maximumAge: 3_600_000 }
      );
   };

   return (
      <>
         <ShopList
            shops={visibleShops}
            imageUrl={imageUrl}
            onRequestRepair={shop => {
               trackShopClick({ shop, action: 'Find Repair Shop Click' });
               setLeadFormShop(shop);
               setTappedShops(visibleShops);
               requestPreciseCoords();
            }}
            onScheduleNow={shop => {
               trackShopClick({ shop, action: 'Schedule Now Click' });
            }}
         />
         {leadFormShop && (
            <LeadFormDialog
               shop={remeasure(leadFormShop, coords)}
               deviceName={deviceName}
               guideTitle={guideTitle}
               guideid={guideid}
               userName={userName}
               userEmail={userEmail}
               recaptchaSiteKey={recaptchaSiteKey}
               recaptchaRef={recaptchaRef}
               onClose={() => setLeadFormShop(null)}
            />
         )}
         <div ref={recaptchaRef} className="hidden" />
      </>
   );
}

/**
 * Shops further away than the cutoff are left out, except the ones already on
 * screen when the user tapped, and the tapped list stands in when nothing
 * clears the cutoff. Both lists were measured from the coordinates we had when
 * they arrived, so measure again before deciding what is in range.
 */
function pickVisibleShops(
   shops: ProShop[],
   tappedShops: ProShop[] | null,
   coords: Coordinates | null
): ProShop[] {
   const tappedIds = new Set(tappedShops?.map(shop => shop.id));
   const visible = nearestFirst(shops, coords).filter(
      shop => shop.distanceMiles <= MAX_DISTANCE_MILES || tappedIds.has(shop.id)
   );
   return visible.length > 0 ? visible : nearestFirst(tappedShops ?? [], coords);
}

/** Remeasured and back in the order the API sends them, nearest first. */
function nearestFirst(shops: ProShop[], coords: Coordinates | null): ProShop[] {
   return shops
      .map(shop => remeasure(shop, coords))
      .sort((a, b) => a.distanceMiles - b.distanceMiles);
}

/**
 * A shop arrives measured from whatever coordinates fetched it, so its
 * distance goes stale the moment the browser hands us better ones. Shops carry
 * their own coordinates, so measure again rather than wait for the next
 * response to land.
 */
function remeasure(shop: ProShop, coords: Coordinates | null): ProShop {
   if (coords === null) {
      return shop;
   }
   return { ...shop, distanceMiles: distanceMilesBetween(coords, shop) };
}

/** Mirrors ProRepairShop::distanceMilesTo, earth radius included. */
function distanceMilesBetween(from: Coordinates, to: Coordinates): number {
   const earthRadiusMiles = 3959;

   const lat1 = toRadians(from.latitude);
   const lon1 = toRadians(from.longitude);
   const lat2 = toRadians(to.latitude);
   const lon2 = toRadians(to.longitude);

   const deltaLat = lat2 - lat1;
   const deltaLon = lon2 - lon1;

   const a =
      Math.sin(deltaLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLon / 2) ** 2;

   return earthRadiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function toRadians(degrees: number): number {
   return (degrees * Math.PI) / 180;
}

type ShopListProps = {
   shops: ProShop[];
   imageUrl: string;
   onRequestRepair: (shop: ProShop) => void;
   onScheduleNow: (shop: ProShop) => void;
};

function ShopList({ shops, imageUrl, onRequestRepair, onScheduleNow }: ShopListProps) {
   return (
      <div className="container mx-auto my-6 rounded-xl w-full overflow-hidden grid grid-rows-[auto_170px] md:grid-cols-2 md:grid-rows-none">
         <div className="p-4 flex flex-col items-start justify-center gap-4 bg-gray-100 md:py-8">
            <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
               {_js('Repair Shops Near You')}
            </p>
            <ul className="m-0 flex w-full list-none flex-col gap-2 p-0">
               {shops.map(shop => (
                  <li key={shop.id}>
                     <ShopRow
                        shop={shop}
                        onRequestRepair={() => onRequestRepair(shop)}
                        onScheduleNow={() => onScheduleNow(shop)}
                     />
                  </li>
               ))}
            </ul>
         </div>
         <div className="relative overflow-hidden">
            <img src={imageUrl} alt="" className="absolute inset-0 size-full object-cover" />
         </div>
      </div>
   );
}

const shopRowClassName = cn(
   'group flex w-full items-center justify-between gap-3 rounded-lg bg-white px-3.5 py-3 text-left',
   'border border-gray-200 transition-colors hover:border-gray-400',
   'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
);

const shopRowIconClassName =
   'size-4 shrink-0 text-gray-400 transition-colors group-hover:text-gray-700';

type ShopRowProps = {
   shop: ProShop;
   onRequestRepair: () => void;
   onScheduleNow: () => void;
};

function ShopRow({ shop, onRequestRepair, onScheduleNow }: ShopRowProps) {
   if (shop.affiliation === 'microcenter') {
      return (
         <a
            href={shop.consultationUrl}
            target="_blank"
            rel="noreferrer"
            onClick={onScheduleNow}
            className={shopRowClassName}
         >
            <ShopSummary shop={shop} />
            <FaIcon name="arrow-up-right-from-square" className={shopRowIconClassName} />
         </a>
      );
   }

   return (
      <button type="button" onClick={onRequestRepair} className={shopRowClassName}>
         <ShopSummary shop={shop} />
         <FaIcon name="chevron-right" className={shopRowIconClassName} />
      </button>
   );
}

function ShopSummary({ shop }: { shop: ProShop }) {
   return (
      <span className="min-w-0">
         <span className="flex flex-wrap items-center gap-x-3">
            <span className="text-base font-semibold text-gray-900">{shop.name}</span>
            <Badge className="badge-brand">
               {_js('%1 mi', Math.round(shop.distanceMiles).toString())}
            </Badge>
         </span>
         <span className="mt-0.5 block text-sm text-gray-600">
            {shop.address}, {shop.city}, {shop.state}
         </span>
      </span>
   );
}

type LeadFormDialogProps = {
   shop: ProShop;
   deviceName: string;
   guideTitle: string;
   guideid: number;
   userName: string | null;
   userEmail: string | null;
   recaptchaSiteKey: string | null;
   recaptchaRef: RefObject<HTMLDivElement | null>;
   onClose: () => void;
};

function LeadFormDialog({
   shop,
   deviceName,
   guideTitle,
   guideid,
   userName,
   userEmail,
   recaptchaSiteKey,
   recaptchaRef,
   onClose,
}: LeadFormDialogProps) {
   const api = useIFixitApiClient();
   const [submitting, setSubmitting] = useState(false);
   const [submitted, setSubmitted] = useState(false);
   const [error, setError] = useState<string | null>(null);

   const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setSubmitting(true);
      setError(null);

      const formData = new FormData(e.currentTarget);

      try {
         let captchaResult: string | undefined;
         if (recaptchaSiteKey) {
            captchaResult = await Recaptcha.executeInvisible(
               recaptchaRef.current,
               recaptchaSiteKey,
               'pro-lead'
            );
         }

         await api.post('lead/submit', 'submit-pro-lead', {
            body: JSON.stringify({
               shopId: shop.id,
               name: formData.get('name'),
               email: formData.get('email'),
               phone: formData.get('phone') || undefined,
               device: formData.get('device') || undefined,
               repair: formData.get('repair') || undefined,
               notes: formData.get('notes') || undefined,
               guideid: guideid || undefined,
               captchaResult,
            }),
         });
         setSubmitted(true);
      } catch {
         setError(_js('Something went wrong. Please try again.'));
      } finally {
         setSubmitting(false);
      }
   };

   return (
      <Dialog
         open
         onOpenChange={open => {
            if (!open) onClose();
         }}
      >
         <DialogContent
            // The form itself is the content, so there is nothing to describe.
            // Radix wants that said out loud instead of pointing at a missing id.
            aria-describedby={undefined}
            className="max-sm:top-0 max-sm:left-0 max-sm:translate-x-0 max-sm:translate-y-0 max-sm:max-w-full max-sm:h-dvh max-sm:rounded-none max-sm:overflow-y-auto"
         >
            <DialogHeader>
               <DialogTitle>{_js('Request a Repair')}</DialogTitle>
            </DialogHeader>

            {submitted ? (
               <div className="py-8 text-center">
                  <p className="text-lg font-medium text-gray-900 mb-2">{_js('Request sent!')}</p>
                  <p className="text-gray-600">
                     {_js('Your selected shop will contact you soon to discuss the repair.')}
                  </p>
                  <Button onClick={onClose} className="mt-6">
                     {_js('Close')}
                  </Button>
               </div>
            ) : (
               <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                  <div className="rounded-lg border border-gray-200 p-3">
                     <div className="flex items-center gap-3">
                        <h3 className="text-base font-semibold text-gray-900">{shop.name}</h3>
                        <Badge className="badge-brand">
                           {_js('%1 mi', Math.round(shop.distanceMiles).toString())}
                        </Badge>
                     </div>
                     <p className="text-sm text-gray-600 mt-1">
                        {shop.address}, {shop.city}, {shop.state} {shop.postcode}
                     </p>
                  </div>
                  <Field
                     label={_js('Device')}
                     name="device"
                     defaultValue={deviceName || undefined}
                  />
                  <Field
                     label={_js('Repair')}
                     name="repair"
                     defaultValue={guideTitle || undefined}
                  />
                  <div className="flex flex-col gap-1">
                     <label className="text-sm font-medium text-gray-700">{_js('Notes')}</label>
                     <textarea
                        name="notes"
                        rows={3}
                        maxLength={2000}
                        className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder={_js('Any additional details about your repair...')}
                     />
                  </div>
                  <Field
                     label={_js('Name')}
                     name="name"
                     defaultValue={userName ?? undefined}
                     required
                  />
                  <Field
                     label={_js('Email')}
                     name="email"
                     type="email"
                     defaultValue={userEmail ?? undefined}
                     required
                  />
                  <Field label={_js('Phone number')} name="phone" type="tel" />

                  {error && <p className="text-sm text-red-600">{error}</p>}

                  <p className="text-gray-500 text-center">
                     {_js('The shop will call or email you directly to discuss the repair.')}
                  </p>

                  <Button type="submit" disabled={submitting} className="mt-2">
                     {submitting ? _js('Submitting...') : _js('Submit Request')}
                  </Button>
                  <p className="text-xs text-gray-500 text-center">
                     {_js(
                        'By submitting, you agree to our Privacy Policy and consent to receive email and text communications from iFixit and the selected repair shop, as applicable.'
                     )}
                  </p>
               </form>
            )}
         </DialogContent>
      </Dialog>
   );
}

type FieldProps = {
   label: string;
   name: string;
   defaultValue?: string;
   type?: string;
   required?: boolean;
};

function Field({ label, name, defaultValue, type = 'text', required }: FieldProps) {
   return (
      <div className="flex flex-col gap-1">
         <label className="text-sm font-medium text-gray-700">{label}</label>
         <input
            name={name}
            type={type}
            defaultValue={defaultValue}
            required={required}
            className={cn(
               'rounded-lg border border-gray-300 px-3 py-2 text-sm',
               'focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'
            )}
         />
      </div>
   );
}

function useNearestProShops({
   latitude,
   longitude,
}: {
   latitude: number | null;
   longitude: number | null;
}) {
   const api = useIFixitApiClient();

   const { data } = useCacheableQuery({
      cacheTime: Duration(1).day,
      schema: ProShopListSchema.nullable(),
      storage: 'localStorage',
      queryKey: ['nearest-pro-shops', latitude, longitude, NEAREST_SHOP_LIMIT],
      queryFn: async () => {
         const coordinates =
            latitude === null || longitude === null ? undefined : { latitude, longitude };
         return api.post<ProShop[] | null>('lead/nearest', 'get-nearest-pro-shops', {
            body: JSON.stringify({ coordinates, limit: NEAREST_SHOP_LIMIT }),
         });
      },
   });

   return data ?? null;
}

function debugCoords(): Coordinates | null {
   const searchParams = new URLSearchParams(window.location.search);
   const latitude = Number(searchParams.get('latitude') ?? 'nan');
   const longitude = Number(searchParams.get('longitude') ?? 'nan');
   if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;
   return { latitude, longitude };
}

function trackShopClick({ shop, action }: { shop: ProShop; action: string }) {
   trackAnalyticsCustomEvent({
      eventCategory: 'Repair Shop Locator',
      eventAction: action,
      eventName: `${shop.name}, ${shop.city}`,
   });
}

initializeComponent('ProLeadSectionComponent', ProLeadSection);
